import { addDoc, collection } from "firebase/firestore";
import { db } from "../Context/firebaseConfig";

const BRAND_NAME = "Kre8 Content";
const BRAND_FROM = "Kre8 Content <support@kre8content.com>";
const SUPPORT_EMAIL = "support@kre8content.com";
const APP_ORIGIN = "https://kre8content.com";

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const safeHeaderText = (value = "") =>
  String(value).replace(/[\r\n]+/g, " ").trim().slice(0, 180);

const normalizeRecipient = (value = "") => String(value).trim();

const isValidRecipient = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeRecipient(value));

const safeKre8Url = (value) => {
  try {
    const url = new URL(value, APP_ORIGIN);
    const isKre8Host =
      url.hostname === "kre8content.com" || url.hostname.endsWith(".kre8content.com");
    return url.protocol === "https:" && isKre8Host ? url.toString() : APP_ORIGIN;
  } catch {
    return APP_ORIGIN;
  }
};

const buildPlainText = ({ headline, intro, details, actionLabel, actionUrl }) => [
  headline,
  "",
  intro,
  "",
  ...details.map(({ label, value }) => `${label}: ${value}`),
  details.length ? "" : null,
  `${actionLabel}: ${actionUrl}`,
  "",
  `This automated account notification was sent by ${BRAND_NAME}.`,
  `Need help? Contact ${SUPPORT_EMAIL}.`,
].filter((line) => line !== null).join("\n");

const buildHtml = ({ preheader, eyebrow, headline, intro, details, actionLabel, actionUrl }) => {
  const detailRows = details.map(({ label, value }) => `
    <tr>
      <td style="padding:10px 12px;color:#71717a;font-size:12px;line-height:18px;text-transform:uppercase;vertical-align:top;width:130px;">${escapeHtml(label)}</td>
      <td style="padding:10px 12px;color:#18181b;font-size:14px;font-weight:600;line-height:20px;vertical-align:top;">${escapeHtml(value)}</td>
    </tr>`).join("");

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
    <meta name="color-scheme" content="light only">
    <title>${escapeHtml(headline)}</title>
  </head>
  <body style="margin:0;padding:0;background:#f4f4f5;color:#18181b;font-family:Arial,Helvetica,sans-serif;">
    <div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">${escapeHtml(preheader)}</div>
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#f4f4f5;">
      <tr>
        <td align="center" style="padding:32px 16px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:600px;background:#ffffff;border:1px solid #e4e4e7;border-radius:8px;overflow:hidden;">
            <tr>
              <td style="height:4px;background:#9146ff;font-size:0;line-height:0;">&nbsp;</td>
            </tr>
            <tr>
              <td style="padding:24px 32px;background:#09090b;color:#ffffff;">
                <span style="font-size:18px;font-weight:800;letter-spacing:0;">KRE8 CONTENT</span>
                <span style="color:#84ff00;font-size:18px;">.</span>
              </td>
            </tr>
            <tr>
              <td style="padding:32px;">
                <p style="margin:0 0 12px;color:#7c3aed;font-size:12px;font-weight:700;line-height:18px;text-transform:uppercase;">${escapeHtml(eyebrow)}</p>
                <h1 style="margin:0 0 14px;color:#18181b;font-size:28px;font-weight:750;line-height:35px;letter-spacing:0;">${escapeHtml(headline)}</h1>
                <p style="margin:0;color:#52525b;font-size:16px;line-height:25px;">${escapeHtml(intro)}</p>
                ${details.length ? `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="margin-top:24px;border:1px solid #e4e4e7;border-radius:6px;background:#fafafa;">${detailRows}</table>` : ""}
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin-top:28px;">
                  <tr>
                    <td style="border-radius:6px;background:#18181b;">
                      <a href="${escapeHtml(actionUrl)}" style="display:inline-block;padding:13px 20px;color:#ffffff;font-size:15px;font-weight:700;line-height:20px;text-decoration:none;">${escapeHtml(actionLabel)}</a>
                    </td>
                  </tr>
                </table>
                <p style="margin:22px 0 0;color:#a1a1aa;font-size:12px;line-height:18px;word-break:break-all;">Button not working? Open ${escapeHtml(actionUrl)}</p>
              </td>
            </tr>
            <tr>
              <td style="padding:20px 32px;border-top:1px solid #e4e4e7;background:#fafafa;color:#71717a;font-size:12px;line-height:18px;">
                This automated account notification was sent because activity occurred on your Kre8 Content account.<br>
                Need help? <a href="mailto:${SUPPORT_EMAIL}" style="color:#52525b;text-decoration:underline;">${SUPPORT_EMAIL}</a>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
};

export function buildTransactionalEmail({
  category,
  subject,
  preheader,
  eyebrow,
  headline,
  intro,
  details = [],
  actionLabel,
  actionUrl,
  importance = "normal",
}) {
  const safeActionUrl = safeKre8Url(actionUrl);
  const safeSubject = safeHeaderText(subject);
  const headers = {
    "Auto-Submitted": "auto-generated",
    "X-Auto-Response-Suppress": "OOF, AutoReply",
    "X-Kre8-Notification": safeHeaderText(category),
  };

  if (importance === "high") {
    headers.Importance = "High";
    headers["X-Priority"] = "1";
  }

  return {
    from: BRAND_FROM,
    replyTo: SUPPORT_EMAIL,
    headers,
    message: {
      subject: safeSubject,
      text: buildPlainText({ headline, intro, details, actionLabel, actionUrl: safeActionUrl }),
      html: buildHtml({
        preheader,
        eyebrow,
        headline,
        intro,
        details,
        actionLabel,
        actionUrl: safeActionUrl,
      }),
    },
  };
}

export const transactionalEmails = {
  chatActivity: ({ senderName = "A Kre8 member", conversationId = "" } = {}) =>
    buildTransactionalEmail({
      category: "chat_activity",
      subject: `New message from ${senderName}`,
      preheader: `${senderName} sent you a message on Kre8 Content.`,
      eyebrow: "Conversation update",
      headline: "You have a new message",
      intro: `${senderName} sent you a message in your Kre8 Content inbox.`,
      details: [{ label: "From", value: senderName }],
      actionLabel: "Open conversation",
      actionUrl: `${APP_ORIGIN}/chat${conversationId ? `?conversationId=${encodeURIComponent(conversationId)}` : ""}`,
    }),

  deliveryReady: ({ orderNumber }) => buildTransactionalEmail({
    category: "delivery_ready",
    subject: `Project ${orderNumber}: delivery ready for review`,
    preheader: `A delivery for project ${orderNumber} is ready for your review.`,
    eyebrow: "Project update",
    headline: "Your delivery is ready",
    intro: "The editor submitted files for your review. Open the project to approve the delivery or request revisions.",
    details: [{ label: "Reference", value: orderNumber }],
    actionLabel: "Review delivery",
    actionUrl: `${APP_ORIGIN}/order/${encodeURIComponent(orderNumber)}`,
    importance: "high",
  }),

  deliveryApproved: ({ orderNumber }) => buildTransactionalEmail({
    category: "delivery_approved",
    subject: `Project ${orderNumber}: delivery approved`,
    preheader: `Your delivery for project ${orderNumber} was approved.`,
    eyebrow: "Project update",
    headline: "Your delivery was approved",
    intro: "The creator approved your delivery. You can open the project workspace to review the completed order.",
    details: [{ label: "Reference", value: orderNumber }],
    actionLabel: "Open project",
    actionUrl: `${APP_ORIGIN}/order/${encodeURIComponent(orderNumber)}`,
  }),

  revisionRequested: ({ orderNumber }) => buildTransactionalEmail({
    category: "revision_requested",
    subject: `Project ${orderNumber}: revisions requested`,
    preheader: `The creator requested revisions for project ${orderNumber}.`,
    eyebrow: "Action required",
    headline: "Revisions were requested",
    intro: "Open the project to review the creator's feedback and upload the revised delivery.",
    details: [{ label: "Reference", value: orderNumber }],
    actionLabel: "Review feedback",
    actionUrl: `${APP_ORIGIN}/order/${encodeURIComponent(orderNumber)}`,
    importance: "high",
  }),

  rewardRequest: ({ username = "Kre8 member", rewardLabel }) => buildTransactionalEmail({
    category: "reward_request",
    subject: "Reward request received",
    preheader: `We received your request for ${rewardLabel}.`,
    eyebrow: "Rewards update",
    headline: "We received your reward request",
    intro: `Hi ${username}, your request is in review. We will follow up within two business days.`,
    details: [
      { label: "Reward", value: rewardLabel },
      { label: "Status", value: "Pending review" },
    ],
    actionLabel: "View rewards",
    actionUrl: `${APP_ORIGIN}/rewards`,
  }),

  youtubeConnected: () => buildTransactionalEmail({
    category: "profile_update",
    subject: "YouTube connected to your creator profile",
    preheader: "Your YouTube connection was added successfully.",
    eyebrow: "Account update",
    headline: "Your YouTube channel is connected",
    intro: "Your creator profile can now display content from the connected YouTube account.",
    details: [{ label: "Connection", value: "YouTube" }],
    actionLabel: "Manage creator profile",
    actionUrl: `${APP_ORIGIN}/creatorPortfolio`,
  }),
};

export async function queueTransactionalEmail({ to, email }) {
  const recipient = normalizeRecipient(to);
  if (!isValidRecipient(recipient)) throw new Error("A valid email recipient is required.");
  if (!email?.from || !email?.replyTo || !email?.message?.subject
    || !email?.message?.text || !email?.message?.html) {
    throw new Error("A complete transactional email payload is required.");
  }

  return addDoc(collection(db, "mail"), { to: recipient, ...email });
}

export const transactionalEmailInternals = {
  escapeHtml,
  isValidRecipient,
  normalizeRecipient,
  safeHeaderText,
  safeKre8Url,
};
